import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { MatLegacyDialog as MatDialog, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';

import { CuttingMaterial } from '../../../../models/cutting/cutting-material.model';
import { FaceBand } from '../../../../models/cutting/cutting-panel-outline.model';
import { CuttingPanel } from '../../../../models/cutting/cutting-panel.model';
import { OutlineEditorOptions } from '../../../../models/user/outline-editor-options.model';
import {
  CuttingMaterialBaseDialogComponent,
  CuttingMaterialBaseDialogData,
} from '../../../cutting-material-base-dialog/cutting-material-base-dialog.component';
import { CuttingPanelListService } from '../../cutting-panel-list.service';

@Component({
  selector: 'csl-face-banding',
  templateUrl: './face-banding.component.html',
  styleUrls: ['./face-banding.component.scss'],
})
export class FaceBandingComponent implements OnInit {
  materialBaseDialogRef: MatDialogRef<CuttingMaterialBaseDialogComponent>;
  outlineOptions: OutlineEditorOptions;

  @Input() editable = true;
  @Input() mainMaterial: CuttingMaterial;
  @Input() panel: CuttingPanel;
  @Input() bands: FaceBand[] = [];
  @Output() formChanged: EventEmitter<FaceBand[]> = new EventEmitter();

  get panelHasButts(): boolean {
    if (!this.panel.l1 && !this.panel.l2 && !this.panel.w1 && !this.panel.w2) {
      return false;
    }
    return true;
  }

  get showQuantityInStock(): boolean {
    return this.cuttingPanelListService.onCuttingOptionsChanged.value.showQuantityInStock;
  }

  constructor(
    public dialog: MatDialog, //
    private cuttingPanelListService: CuttingPanelListService
  ) {
    this.cuttingPanelListService.onCuttingOptionsChanged.subscribe(response => {
      this.outlineOptions = response.outlineEditorOptions;
    });
  }

  ngOnInit(): void {}

  openMaterialBaseDialog(pos: '+' | '-'): void {
    if (this.outlineOptions.faceBanding.cadAddOtherMaterial === true) {
      this.materialBaseDialogRef = this.dialog.open(CuttingMaterialBaseDialogComponent, {
        panelClass: 'material-base-material-list-dialog',
        disableClose: false,
        width: '1150px',
        data: {
          materialsToExclude: [],
          showQuantityInStock: this.showQuantityInStock,
        } as CuttingMaterialBaseDialogData,
      });

      this.materialBaseDialogRef.afterClosed().subscribe((result: CuttingMaterial[] | undefined) => {
        if (result && result.length > 0) {
          result.forEach(m => {
            this.addFaceBand(m, pos);
          });
        }
        this.materialBaseDialogRef = null;
      });
    } else {
      const newMaterial = new CuttingMaterial(this.mainMaterial);
      newMaterial.panels = [];
      newMaterial.usedOffcuts = [];
      newMaterial.offcuts = [];

      this.addFaceBand(newMaterial, pos);
    }
  }

  private addFaceBand(material: CuttingMaterial, pos: '+' | '-'): void {
    let nextLevel = 0;

    if (this.bands.length > 0) {
      if (pos === '+' && Math.max(...this.bands.map(b => b.level)) > 0) {
        nextLevel = Math.max(...this.bands.map(b => b.level));
      } else if (pos === '-' && Math.min(...this.bands.map(b => b.level)) < 0) {
        nextLevel = this.bands.length > 0 ? Math.min(...this.bands.map(b => b.level)) : 0;
      }
    }

    if (pos === '+') {
      nextLevel++;
    } else {
      nextLevel--;
    }

    this.bands.push({
      level: nextLevel,
      material: material,
      order: this.bands.length + 1,
    } as FaceBand);

    this.bands.sort((a, b) => b.level - a.level);

    this.formChanged.emit(this.bands);
  }

  deleteBand(band: FaceBand): void {
    const bandIndex = this.bands.findIndex(b => b.level === band.level);

    if (bandIndex !== -1) {
      this.bands.splice(bandIndex, 1);
      this.recalcOrder();
      this.formChanged.emit(this.bands);
    }
  }

  private recalcOrder(): void {
    const orders = this.bands.map(b => b.order).sort((a, b) => b - a);
    let noneVal = 0;

    for (let i = orders.length - 1; i > 0; i--) {
      if (orders[i] - orders[i - 1] > 1) {
        noneVal = orders[i] - 1;
        break;
      }
    }

    this.bands.forEach(band => {
      if (band.order > noneVal) {
        band.order--;
      }
    });
  }
}
